import { DBricksConnection } from '../workflow/connections/databricks'
import {
  ColumnDataType,
  WorkflowComponentCodeParams,
  Provider,
  TableSchema,
  QueryAndOutputs,
  WorkflowCodeType,
  WorkflowComponentParamType,
  WorkflowComponentParameterMode,
  Language,
  WorkflowComponentOutputTablesParams
} from '../types'
import { WorkflowComponent } from './common/workflowcomponent'
import { dedent } from 'ts-dedent'
import { SINGLE_OUTPUT_NAME } from './common/singleoutputcomponent'

class CreateVectorTileset extends WorkflowComponent {
  constructor() {
    super(
      {
        name: 'native.createvectortileset',
        group: 'Tileset Creation',
        title: 'Create Vector Tileset',
        description: `
        This component creates a tileset from an input containing Point, Lines or Polygons.
      `,
        starred: true
      },
      [
        {
          name: 'source',
          type: WorkflowComponentParamType.InputTable,
          title: 'Source table'
        },
        {
          name: 'destination',
          type: WorkflowComponentParamType.OutputTable,
          title: 'Table details',
          placeholder: 'Rename and select destination'
        },
        {
          name: 'geom_column',
          type: WorkflowComponentParamType.Column,
          title: 'Geo column',
          parent: 'source',
          dataType: [ColumnDataType.Geography, ColumnDataType.Geometry]
        },
        {
          name: 'zoom_min',
          type: WorkflowComponentParamType.Number,
          title: 'Minimum zoom level',
          min: 0,
          max: 18,
          mode: WorkflowComponentParameterMode.Slider,
          default: 0,
          allowExpressions: false
        },
        {
          name: 'zoom_max',
          type: WorkflowComponentParamType.Number,
          title: 'Maximum zoom level',
          min: 1,
          max: 20,
          mode: WorkflowComponentParameterMode.Slider,
          allowExpressions: false,
          default: 12
        },
        {
          name: 'properties',
          type: WorkflowComponentParamType.Column,
          mode: WorkflowComponentParameterMode.Multiple,
          title: 'Properties',
          parent: 'source',
          dataType: [ColumnDataType.Number, ColumnDataType.String],
          allowExpressions: false,
          noDefault: true,
          optional: true
        },
        {
          name: 'zoom_min_col',
          title: 'Minimum zoom column',
          type: WorkflowComponentParamType.Column,
          parent: 'source',
          dataType: [ColumnDataType.Number, ColumnDataType.String],
          providers: [Provider.BigQuery],
          default: '',
          advanced: true,
          optional: true,
          allowExpressions: false,
          helper: 'workflows.parameterForm.zoomMinColHelper'
        },
        {
          name: 'zoom_max_col',
          title: 'Maximum zoom column',
          type: WorkflowComponentParamType.Column,
          parent: 'source',
          dataType: [ColumnDataType.Number, ColumnDataType.String],
          providers: [Provider.BigQuery],
          default: '',
          advanced: true,
          optional: true,
          allowExpressions: false,
          helper: 'workflows.parameterForm.zoomMaxColHelper'
        },
        {
          name: 'max_tile_vertices',
          type: WorkflowComponentParamType.Number,
          title: 'Maximum vertices per tile',
          providers: [Provider.Snowflake, Provider.Redshift, Provider.DatabricksRest],
          min: 0,
          optional: true,
          allowExpressions: false,
          advanced: true
        },
        {
          name: 'max_tile_features',
          type: WorkflowComponentParamType.Number,
          title: 'Maximum features per tile',
          min: 0,
          allowExpressions: false,
          optional: true,
          advanced: true
        },
        {
          name: 'max_tile_size_kb',
          type: WorkflowComponentParamType.Number,
          title: 'Maximum kilobytes per tile',
          min: 1,
          max: 6 * 1024,
          providers: [Provider.BigQuery, Provider.Postgres],
          allowExpressions: false,
          optional: true,
          advanced: true
        },
        {
          name: 'tile_feature_order',
          type: WorkflowComponentParamType.String,
          title: 'Tile feature order',
          placeholder: 'E.g.: ST_AREA(geom) DESC',
          optional: true,
          advanced: true,
          allowExpressions: false,
          helper: 'workflows.parameterForm.tileFeatureOrderHelper'
        },
        {
          name: 'calculate_geoids',
          type: WorkflowComponentParamType.Boolean,
          title: 'Generate this tileset as a custom boundary for CARTO + deck.gl',
          default: false,
          providers: [Provider.BigQuery, Provider.Snowflake],
          advanced: true
        },
        {
          name: 'include_geoids',
          type: WorkflowComponentParamType.Boolean,
          title: 'Generate this tileset as a custom boundary for CARTO + deck.gl',
          default: false,
          providers: [Provider.DatabricksRest],
          advanced: true
        },
        {
          name: 'metadata',
          type: WorkflowComponentParamType.Json,
          title: 'Metadata',
          placeholder: 'E.g.: { "name": "...", "description": "..." }',
          default: '',
          allowExpressions: false,
          optional: true,
          advanced: true
        },
        {
          name: 'max_categories_limit',
          type: WorkflowComponentParamType.Number,
          title: 'Max categories generated',
          min: 1,
          allowExpressions: false,
          optional: true,
          advanced: true,
          requiredAnalyticsToolboxVersion: {
            [Provider.BigQuery]: '1.22.2',
            [Provider.Snowflake]: '1.20.2',
            [Provider.Redshift]: '1.11.2',
            [Provider.Postgres]: '1.4.1'
          }
        }
      ],
      []
    )
  }

  /**
   * Generates a SQL command to manipulate tilesets based on the provided parameters.
   * This function supports both dry run and actual execution modes.
   *
   * @param {WorkflowComponentSQLParams} params - The parameters needed to generate the SQL command.
   * @returns {Promise<QueryAndOutputs | null>} A promise that resolves to the SQL query and output settings
   */
  async getCode({
    workflow,
    params,
    connection,
    envVars,
    componentName,
    codeType,
    schemas
  }: WorkflowComponentCodeParams): Promise<QueryAndOutputs | null> {
    let query: string
    const preparedParams = this._prepareParameters(params)
    const isDryRun = codeType === WorkflowCodeType.DryRun

    let properties = connection.type === Provider.DatabricksRest ? '' : {}
    if (preparedParams.properties) {
      if (connection.type === Provider.DatabricksRest) {
        properties = (preparedParams.properties as Array<string>).join('; ')
      } else {
        properties = (preparedParams.properties as Array<string>).reduce((obj, key) => {
          let type: string = (schemas?.input.source as TableSchema)?.find((item) => item.name === key)?.type as string
          type = type ? type.charAt(0).toUpperCase() + type.slice(1) : 'String'
          return {
            ...obj,
            [key]: type
          }
        }, {})
      }
    }

    const options = {
      zoom_min: Math.floor(preparedParams.zoom_min as number),
      zoom_max: Math.floor(preparedParams.zoom_max as number),
      geom_column: preparedParams.geom_column,
      max_tile_features: preparedParams.max_tile_features
        ? Math.floor(preparedParams.max_tile_features as number)
        : undefined,
      tile_feature_order:
        preparedParams.tile_feature_order || (connection.type == Provider.BigQuery ? '(SELECT 1)' : ''), // ORDER BY 1 functionally identical to no ORDER BY clause
      properties: properties,
      metadata: preparedParams.metadata ? JSON.parse(preparedParams.metadata as string) : {},
      max_categories_limit: preparedParams.max_categories_limit
        ? Math.floor(preparedParams.max_categories_limit as number)
        : undefined
    }
    switch (connection.type) {
      case Provider.BigQuery:
        options['max_tile_size_strategy'] = 'drop_fraction_as_needed'
        options['drop_duplicates'] = false
        options['zoom_min_column'] = preparedParams.zoom_min_col
        options['zoom_max_column'] = preparedParams.zoom_max_col
        options['max_tile_size_kb'] = preparedParams.max_tile_size_kb
          ? Math.floor(preparedParams.max_tile_size_kb as number)
          : undefined
        options['calculate_geoids'] = preparedParams.calculate_geoids
        break
      case Provider.Snowflake:
        options['max_tile_size_strategy'] = 'drop_fraction_as_needed'
        options['max_tile_vertices'] = preparedParams.max_tile_vertices
          ? Math.floor(preparedParams.max_tile_vertices as number)
          : undefined
        options['calculate_geoids'] = preparedParams.calculate_geoids
        break
      case Provider.Redshift:
        options['max_tile_size_strategy'] = 'drop_fraction_as_needed'
        options['max_tile_vertices'] = preparedParams.max_tile_vertices
          ? Math.floor(preparedParams.max_tile_vertices as number)
          : undefined
        break
      case Provider.Postgres:
        options['max_tile_size_strategy'] = 'drop_fraction_as_needed'
        options['max_tile_size_kb'] = preparedParams.max_tile_size_kb
          ? Math.floor(preparedParams.max_tile_size_kb as number)
          : undefined
        break
      case Provider.DatabricksRest:
        options['if_exists'] = 'replace'
        options['max_tile_vertices'] = preparedParams.max_tile_vertices
          ? Math.floor(preparedParams.max_tile_vertices as number)
          : undefined
        options['include_geoids'] = preparedParams.include_geoids
        break
      default:
        break
    }

    // Remove any null/empty options
    Object.keys(options).forEach((key) => (options[key] == null || options[key] == '') && delete options[key])

    const quotedSource = connection.quoteName(preparedParams.source as string)
    const quotedDestination = connection.quoteName(preparedParams.destination as string)

    const quotedJsonOptions =
      connection.type === Provider.DatabricksRest
        ? `'${JSON.stringify(options)}'`
        : connection.quoteMultilineString(JSON.stringify(options))

    if (isDryRun) {
      if (connection.type === Provider.BigQuery) {
        const geoidColumn = preparedParams.calculate_geoids ? `['geoid'] AS geoids` : ''
        query = connection.getCreateTempTableQuery(
          quotedDestination,
          `SELECT 0 AS z, 0 AS x, 0 AS y, 0 AS carto_partition, B"abc" AS data, ${geoidColumn}`,
          codeType,
          null
        )
      } else {
        const geoidColumn =
          connection.type === Provider.Snowflake && preparedParams.calculate_geoids ? `, 'geoid'::VARCHAR AS geoid` : ''

        query = connection.getCreateTempTableQuery(
          quotedDestination,
          `SELECT 0 AS z, 0 AS x, 0 AS y, CAST(NULL AS BINARY) AS data ${geoidColumn}`,
          codeType,
          null
        )

        if (connection.type === Provider.DatabricksRest) {
          query = (connection as DBricksConnection).wrapSQLCodeAsPythonCall(query)
        }
      }
    } else {
      switch (connection.type) {
        case Provider.BigQuery:
        case Provider.Snowflake:
        case Provider.Postgres:
          query = dedent`
          ${connection.getDropTableQuery(quotedDestination)}
          CALL ${connection.quoteName(envVars.analyticsToolboxDataset as string)}.CREATE_SIMPLE_TILESET(
             '${quotedSource}',
             '${quotedDestination}',
             ${quotedJsonOptions}
          );
          `
          break
        case Provider.Redshift:
          // Redshift needs destination as variable, not hardcoded string
          query = dedent`
        DECLARE rsDestTable TEXT;
        BEGIN
        rsDestTable := '${quotedDestination}';
        EXECUTE 'DROP TABLE IF EXISTS ' || rsDestTable || ' CASCADE;';
        CALL ${connection.quoteName(envVars.analyticsToolboxDataset as string)}.CREATE_SIMPLE_TILESET(
    '${quotedSource}',
    rsDestTable,
    ${quotedJsonOptions}
        );
        END;
      `
          break
        case Provider.DatabricksRest:
          query = `
spark.read.format("com.carto.analytics.toolbox.stored.procedures.ATStoredProcedureDatasource").option(
      "sql",
      """
      CALL_CARTO carto_un.carto.CREATE_VECTOR_TILESET(

        '${quotedSource}',
        '${quotedDestination}',
        ${quotedJsonOptions}
        );
      """
    ).load().collect();`
          break
        default:
          return null
      }
    }

    // Create a dummy table to be able to retrieve the status of the node.
    const tablename = connection.getOutputTableName(workflow.id, componentName, params, SINGLE_OUTPUT_NAME, codeType)
    let createTable = connection.getCreateTempTableQuery(tablename, `SELECT 1 AS N`, codeType, null)
    if (connection.type === Provider.DatabricksRest) {
      createTable = (connection as DBricksConnection).wrapSQLCodeAsPythonCall(createTable)
    }
    query += `\n${createTable}`

    return {
      outputs: {
        result: connection.applyCapitalization(preparedParams.destination as string)
      },
      query
    }
  }

  getOutputTables({
    workflow,
    params,
    connection,
    componentName,
    codeType
  }: WorkflowComponentOutputTablesParams): Record<string, string> {
    const tablename = connection.getOutputTableName(workflow.id, componentName, params, SINGLE_OUTPUT_NAME, codeType)
    return {
      [SINGLE_OUTPUT_NAME]: tablename
    }
  }

  language(provider: Provider): Language {
    if (provider === Provider.DatabricksRest) {
      return Language.Python
    }
    return Language.SQL
  }

  requiredAnalyticsToolboxVersion(provider: Provider): string | null {
    return '1.0.0'
  }

  getVersion(provider: Provider) {
    return '1'
  }
  supportedProviders(): Provider[] {
    return [Provider.BigQuery, Provider.Postgres, Provider.Redshift, Provider.Snowflake, Provider.DatabricksRest]
  }
}

export { CreateVectorTileset }
